import { format } from "date-fns";
import React from "react";

import { Text, TextDate } from "@performyard/py-components";
import { GetReviewCycleDetailsResponseFrontend } from "@performyard/service-contracts";
import { setTimeOfDateInTimezone } from "@performyard/utils-isomorphic";

import { useReviewCycleBuilderPageContext } from "../../hooks/ReviewCycleBuilderPageContext";

import BaseReviewCycleDetailsCard from "../BaseReviewCycleDetailsCard";

const getRuntime = (autoflow: GetReviewCycleDetailsResponseFrontend["autoflow"]) => {
	if (!autoflow || autoflow.trigger.type !== "scheduled") {
		return "N/A";
	}

	// Recurring cron
	if (autoflow.trigger.scheduled.recurring) {
		if (autoflow.trigger.scheduled.expression.scheduledCron === "0 0 * * ? *") {
			const timezone = autoflow.trigger.scheduled.timezone;
			const relativeMidnight = setTimeOfDateInTimezone({ date: new Date(), timezone });

			return `Daily at ${format(relativeMidnight, "h:mm a")}`;
		}

		return "Daily at 12:00 am";
	}

	// One-time timestamp
	if (autoflow.trigger.scheduled.expression.scheduledTime) {
		return (
			<TextDate
				size="paragraph-3"
				dateSize="medium"
				date={new Date(autoflow.trigger.scheduled.expression.scheduledTime)}
				overrideElement="span"
				hasTime={true}
			></TextDate>
		);
	}
};

const getStatus = (autoflow: GetReviewCycleDetailsResponseFrontend["autoflow"]) => {
	if (!autoflow) {
		return null;
	}

	if (autoflow.trigger.type === "scheduled") {
		if (autoflow.trigger.scheduled.recurring) {
			if (
				autoflow.trigger.scheduled.endTimeDate &&
				new Date(autoflow.trigger.scheduled.endTimeDate) < new Date()
			) {
				return "Disabled";
			}
		} else {
			if (
				autoflow.trigger.scheduled.expression.scheduledTime &&
				new Date(autoflow.trigger.scheduled.expression.scheduledTime) < new Date()
			) {
				return "Disabled";
			}
		}
	}

	return autoflow.enabled ? "Enabled" : "Disabled";
};

const ReviewAutomationCard = () => {
	const { reviewCycleDetails } = useReviewCycleBuilderPageContext();

	const autoflow = reviewCycleDetails?.autoflow;

	if (!autoflow || autoflow.trigger.type !== "scheduled") {
		return null;
	}

	const type = autoflow.trigger.scheduled.recurring ? "Rolling Automation" : "One-Time Automation";
	const status = getStatus(autoflow);
	const runtime = getRuntime(autoflow);
	const endDate = autoflow.trigger.scheduled.endTimeDate;

	return (
		<BaseReviewCycleDetailsCard width="normal">
			<div className="flex flex-col gap-4">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					Review Automation
				</Text>
				<div className="flex flex-col gap-2">
					<div className="flex gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="span">
							Type:
						</Text>{" "}
						<Text size="paragraph-3" weight="regular" color="neutral-9">
							{type}
						</Text>
					</div>
					<div className="flex gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="span">
							Status:
						</Text>{" "}
						<Text size="paragraph-3" weight="regular" color="neutral-9">
							{status}
						</Text>
					</div>
					<div className="flex gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="span">
							Runtime:
						</Text>{" "}
						<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
							{runtime}
						</Text>
					</div>
					<div className="flex gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="span">
							Automation End Date:
						</Text>{" "}
						{endDate ? (
							<TextDate
								size="paragraph-3"
								dateSize="medium"
								date={endDate}
								overrideElement="span"
								hasTime={false}
							></TextDate>
						) : (
							<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
								None
							</Text>
						)}
					</div>
				</div>
			</div>
		</BaseReviewCycleDetailsCard>
	);
};

export default ReviewAutomationCard;
